import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/Common/badge';
import ParsedAssistantMessage from '@/components/Common/ParsedAssistantMessage';

interface Source {
  document_name: string;
  document_id: string;
  text: string;
}

interface StreamingMessageProps {
  thinking: string;
  sources: Source[];
  currentMessage: string;
}

export const StreamingMessage: React.FC<StreamingMessageProps> = ({
  thinking,
  sources,
  currentMessage,
}) => {
  const { t } = useTranslation();
  return (
    <div className="w-full py-1">
      {!sources.length && !currentMessage && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {thinking || t('chatPage.searching')}
        </div>
      )}

      {sources.length > 0 && !currentMessage && (
        <div className="space-y-3 text-[15px] leading-relaxed">
          <div className="text-sm text-muted-foreground">{t('chatPage.sourcesFound')}</div>
          <div className="flex flex-wrap gap-2">
            {sources.map((source, idx) => (
              <Badge key={idx} variant="outline" className="text-xs font-normal">
                {source.document_name}
              </Badge>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('chatPage.generatingAnswer')}
          </div>
        </div>
      )}

      {currentMessage && (
        <div className="text-[15px] leading-relaxed">
          {sources.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-1.5">
              {sources.map((source, idx) => (
                <Badge key={idx} variant="outline" className="text-xs font-normal">
                  {source.document_name}
                </Badge>
              ))}
            </div>
          )}
          <div className="relative">
            <ParsedAssistantMessage content={currentMessage} className="text-[15px] leading-relaxed" />
            <span className="ml-1 inline-block h-4 w-0.5 animate-pulse bg-primary align-middle" />
          </div>
        </div>
      )}
    </div>
  );
};
